import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, LogOut, Plus, Edit, Trash2, MapPin, Users, TrendingUp,
  Zap, BarChart3, Globe, ChevronDown, ChevronUp, X, Loader2, Target, AlertTriangle, Map, LayoutGrid
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import { ExpansionHeatmap } from '../components/ExpansionHeatmap';

const STATUS_CONFIG = {
  live: { label: 'Live', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  priority: { label: 'Priority', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  early_interest: { label: 'Early Interest', bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
};

const LABEL_CONFIG = {
  recommended_next_launch: { label: 'Ready to Launch', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  close_to_launch: { label: 'Close to Launch', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  needs_more_employers: { label: 'Needs Employers', color: 'text-orange-700 bg-orange-50 border-orange-200' },
  needs_more_trades: { label: 'Needs Trades', color: 'text-purple-700 bg-purple-50 border-purple-200' },
  too_early: { label: 'Too Early', color: 'text-slate-500 bg-slate-50 border-slate-200' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.early_interest;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function RecommendationBadge({ label }) {
  const cfg = LABEL_CONFIG[label] || LABEL_CONFIG.too_early;
  return (
    <span data-testid={`rec-badge-${label}`} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function MetricCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4 text-slate-400" />
        <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function AreaCard({ area, onEdit, onDelete, onStatusChange }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div data-testid={`area-card-${area.region_id}`} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{area.area_name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={area.area_status} />
              <RecommendationBadge label={area.recommendation_label} />
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => onEdit(area)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" data-testid={`edit-area-${area.region_id}`}>
              <Edit className="h-4 w-4" />
            </button>
            <button onClick={() => onDelete(area.region_id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-area-${area.region_id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-slate-900">{area.tradesperson_count}</p>
            <p className="text-xs text-slate-500">Trades</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-slate-900">{area.employer_count}</p>
            <p className="text-xs text-slate-500">Employers</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-orange-600">{area.launch_readiness_score}</p>
            <p className="text-xs text-slate-500">Score</p>
          </div>
          <div className="text-center p-2 bg-slate-50 rounded-lg">
            <p className="text-lg font-bold text-slate-900">{area.trades_to_employers_ratio}</p>
            <p className="text-xs text-slate-500">S/D Ratio</p>
          </div>
        </div>

        {area.postcode_prefixes?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {area.postcode_prefixes.map((p, i) => (
              <span key={`${area.region_id}-prefix-${p}`} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded font-mono">{p}</span>
            ))}
          </div>
        )}

        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mt-2" data-testid={`expand-area-${area.region_id}`}>
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? 'Less detail' : 'More detail'}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-slate-100 p-5 bg-slate-50/50 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Urgent Trades:</span> <span className="font-medium">{area.urgent_tradesperson_count}</span></div>
            <div><span className="text-slate-500">Urgent Employers:</span> <span className="font-medium">{area.urgent_employer_count}</span></div>
            <div><span className="text-slate-500">7-day growth:</span> <span className="font-medium text-emerald-600">+{area.growth_last_7_days}</span></div>
            <div><span className="text-slate-500">30-day growth:</span> <span className="font-medium">+{area.growth_last_30_days}</span></div>
            <div><span className="text-slate-500">Trade variety:</span> <span className="font-medium">{area.trade_variety} types</span></div>
            <div><span className="text-slate-500">Total waitlist:</span> <span className="font-medium">{area.total_waitlist_count}</span></div>
          </div>
          {area.top_trades?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 mb-1.5 font-medium">Top Trades</p>
              <div className="flex flex-wrap gap-1.5">
                {area.top_trades.map((t, i) => (
                  <span key={`${area.region_id}-trade-${t.trade}`} className="px-2 py-0.5 bg-white border border-slate-200 text-slate-700 text-xs rounded">{t.trade} ({t.count})</span>
                ))}
              </div>
            </div>
          )}
          {area.area_status !== 'live' && (
            <div className="pt-2 flex gap-2">
              {area.area_status !== 'priority' && (
                <button onClick={() => onStatusChange(area.region_id, 'priority')} className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors" data-testid={`promote-priority-${area.region_id}`}>
                  Promote to Priority
                </button>
              )}
              <button onClick={() => onStatusChange(area.region_id, 'live')} className="text-xs px-3 py-1.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors" data-testid={`promote-live-${area.region_id}`}>
                Go Live
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminRegions() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [areas, setAreas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryV2, setSummaryV2] = useState(null);
  const [areasV2, setAreasV2] = useState([]);
  const [areaView, setAreaView] = useState('all'); // all|live|priority|expansion|unmapped
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('cards');
  const [formData, setFormData] = useState({
    name: '', status: 'early_interest', county: '', town_city: '',
    postcode_prefixes: '', postcode_districts: '', notes: ''
  });

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (res.data.user_type !== 'admin') { navigate('/login'); return; }
      setUser(res.data);
    } catch { navigate('/login'); }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const [areasRes, summaryRes, summaryV2Res, areasV2Res] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/expansion/areas`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/expansion/summary`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/expansion/summary-v2`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/expansion/areas-v2`, { withCredentials: true }).catch(() => ({ data: { areas: [] } })),
      ]);
      setAreas(areasRes.data.areas || []);
      setSummary(summaryRes.data);
      setSummaryV2(summaryV2Res.data);
      setAreasV2(areasV2Res.data?.areas || []);
    } catch (err) {
      toast.error('Failed to fetch expansion data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { if (user) fetchData(); }, [user, fetchData]);

  // Merge legacy waitlist-only area data with v2 user counts so the
  // heat map reflects signed-up users, not just waitlist entries.
  // Also normalise legacy status="active" → "live" for visual treatment.
  const mergedHeatmapAreas = React.useMemo(() => {
    const v2ById = new Map((areasV2 || []).map(a => [a.region_id, a]));
    return (areas || []).map(a => {
      const v2 = v2ById.get(a.region_id) || {};
      const userTrades = v2?.tradespeople?.users || 0;
      const userEmployers = v2?.employers?.users || 0;
      const wlTrades = a.tradesperson_count || 0;
      const wlEmployers = a.employer_count || 0;
      // Normalise legacy "active" → "live" so the bubble colours/sizes
      // treat existing live regions correctly.
      const normalisedStatus = (a.status === "active" || a.status === "live") ? "live" : a.status;
      return {
        ...a,
        area_status: normalisedStatus,
        // total_waitlist_count is the field the heatmap reads for bubble
        // size + colour. Replace the waitlist-only number with the
        // combined user + waitlist total so out-of-region signups appear.
        total_waitlist_count: userTrades + userEmployers + wlTrades + wlEmployers,
        tradesperson_count: userTrades + wlTrades,
        employer_count: userEmployers + wlEmployers,
      };
    });
  }, [areas, areasV2]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Area name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        status: formData.status,
        county: formData.county || null,
        town_city: formData.town_city || null,
        notes: formData.notes || null,
        postcode_prefixes: formData.postcode_prefixes.split(',').map(p => p.trim().toUpperCase()).filter(Boolean),
        postcode_districts: formData.postcode_districts.split(',').map(p => p.trim().toUpperCase()).filter(Boolean),
      };
      if (editingArea) {
        await axios.put(`${BACKEND_URL}/api/admin/regions/${editingArea.region_id}`, payload, { withCredentials: true });
        toast.success('Area updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/regions`, payload, { withCredentials: true });
        toast.success('Area created');
      }
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save area');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (area) => {
    setEditingArea(area);
    setFormData({
      name: area.area_name || '',
      status: area.area_status || 'early_interest',
      county: area.county || '',
      town_city: area.town_city || '',
      postcode_prefixes: (area.postcode_prefixes || []).join(', '),
      postcode_districts: (area.postcode_districts || []).join(', '),
      notes: area.notes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (regionId) => {
    if (!window.confirm('Delete this area? Waitlist entries will be unlinked.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/regions/${regionId}`, { withCredentials: true });
      toast.success('Area deleted');
      fetchData();
    } catch { toast.error('Failed to delete area'); }
  };

  const handleStatusChange = async (regionId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/regions/${regionId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Area status changed to ${newStatus}`);
      fetchData();
    } catch { toast.error('Failed to update status'); }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingArea(null);
    setFormData({ name: '', status: 'early_interest', county: '', town_city: '', postcode_prefixes: '', postcode_districts: '', notes: '' });
  };

  const filteredAreas = statusFilter === 'all' ? areas : areas.filter(a => a.area_status === statusFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
              <div className="flex items-center gap-2">
                <Globe className="h-7 w-7 text-orange-500" />
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Expansion Intelligence</h1>
                  <p className="text-xs text-slate-500">Regional rollout management</p>
                </div>
              </div>
            </div>
            <button onClick={() => navigate('/admin/waitlist')} className="text-sm px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors" data-testid="view-waitlist-btn">
              View Waitlist
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="expansion-summary">
            <MetricCard icon={Users} label="Total Waitlist" value={summary.total_waitlist} testId="metric-total-waitlist" />
            <MetricCard icon={Target} label="Tradespeople" value={summary.total_tradespeople} testId="metric-trades" />
            <MetricCard icon={Target} label="Employers" value={summary.total_employers} testId="metric-employers" />
            <MetricCard icon={Zap} label="Live Areas" value={summary.live_areas} testId="metric-live" />
            <MetricCard icon={TrendingUp} label="Priority" value={summary.priority_areas} testId="metric-priority" />
            <MetricCard icon={BarChart3} label="Early Interest" value={summary.early_interest_areas} testId="metric-early" />
            {summary.unmapped_entries > 0 && (
              <MetricCard icon={MapPin} label="Unmapped" value={summary.unmapped_entries} testId="metric-unmapped" />
            )}
          </div>
        )}

        {/* v2 — counts users + waitlist together */}
        {summaryV2 && (
          <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="expansion-summary-v2">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="font-semibold text-slate-900">Signed-up users by region status</h3>
              <span className="text-xs text-slate-500">
                Total active users: <strong className="text-slate-800">{summaryV2.users.total}</strong>
                {' · '}out-of-region signups: <strong className="text-orange-700">{summaryV2.out_of_region_signups}</strong>
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KindCard label="Live area" tone="emerald" data={summaryV2.users.by_region_kind.live}
                onClick={() => setAreaView('live')} active={areaView === 'live'} testId="kindcard-live" />
              <KindCard label="Priority" tone="amber" data={summaryV2.users.by_region_kind.priority}
                onClick={() => setAreaView('priority')} active={areaView === 'priority'} testId="kindcard-priority" />
              <KindCard label="Out of live area" tone="orange" data={summaryV2.users.by_region_kind.expansion}
                onClick={() => setAreaView('expansion')} active={areaView === 'expansion'} testId="kindcard-expansion" />
              <KindCard label="Unmapped" tone="slate" data={summaryV2.users.by_region_kind.unmapped}
                onClick={() => setAreaView('unmapped')} active={areaView === 'unmapped'} testId="kindcard-unmapped" />
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <ToggleChip label="All areas" active={areaView === 'all'} onClick={() => setAreaView('all')} testId="toggle-all" />
              <ToggleChip label="Live areas only" active={areaView === 'live'} onClick={() => setAreaView('live')} testId="toggle-live" />
              <ToggleChip label="Out-of-region signups" active={areaView === 'expansion'} onClick={() => setAreaView('expansion')} testId="toggle-expansion" />
              <ToggleChip label="Priority" active={areaView === 'priority'} onClick={() => setAreaView('priority')} testId="toggle-priority" />
              <ToggleChip label="Unmapped" active={areaView === 'unmapped'} onClick={() => setAreaView('unmapped')} testId="toggle-unmapped" />
            </div>
            {areaView !== 'all' && areasV2.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wider">
                  {areaView === 'expansion' ? 'Expansion / out-of-region areas' :
                   areaView === 'live' ? 'Live areas' :
                   areaView === 'priority' ? 'Priority areas' : 'Areas'}
                </p>
                <div className="space-y-1">
                  {areasV2
                    .filter(a => {
                      if (areaView === 'expansion') return a.status === 'early_interest';
                      if (areaView === 'live') return a.status === 'live';
                      if (areaView === 'priority') return a.status === 'priority';
                      return true;
                    })
                    .map(a => (
                      <div key={a.region_id} className="flex flex-wrap items-center justify-between gap-2 p-2 bg-slate-50 rounded text-sm" data-testid={`area-row-${a.region_id}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{a.name}</span>
                          {a.country && <span className="text-xs text-slate-500">· {a.country}</span>}
                          {a.auto_seeded && <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">auto-seeded</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-600">
                          <span>{a.tradespeople.total} trades</span>
                          <span>{a.employers.total} employers</span>
                          <span className="text-slate-400">score {a.combined_score}</span>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommended Next Launch */}
        {summary?.recommended_next_launch && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5" data-testid="recommended-launch">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-5 w-5 text-emerald-600" />
              <h3 className="font-bold text-emerald-800">Recommended Next Launch</h3>
            </div>
            <p className="text-emerald-700">
              <strong>{summary.recommended_next_launch.area_name}</strong> — Score: {summary.recommended_next_launch.launch_readiness_score} | {summary.recommended_next_launch.tradesperson_count} trades, {summary.recommended_next_launch.employer_count} employers
            </p>
          </div>
        )}

        {/* View Toggle + Filter Bar + Add */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* View Mode Tabs */}
            <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
              <button onClick={() => setActiveTab('cards')} data-testid="tab-cards"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'cards' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <LayoutGrid className="h-3.5 w-3.5" /> Cards
              </button>
              <button onClick={() => setActiveTab('heatmap')} data-testid="tab-heatmap"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === 'heatmap' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                <Map className="h-3.5 w-3.5" /> Heatmap
              </button>
            </div>

            {/* Status Filters (cards view only) */}
            {activeTab === 'cards' && (
              <div className="flex items-center gap-2">
                {['all', 'live', 'priority', 'early_interest'].map(s => (
                  <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s}`}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? 'bg-orange-500 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
                    {s === 'all' ? `All (${areas.length})` : `${STATUS_CONFIG[s]?.label} (${areas.filter(a => a.area_status === s).length})`}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium" data-testid="add-area-btn">
            <Plus className="h-4 w-4" /> Add Area
          </button>
        </div>

        {/* Area Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={resetForm}>
            <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900">{editingArea ? 'Edit Area' : 'Add New Area'}</h2>
                <button onClick={resetForm} className="text-slate-400 hover:text-slate-600" data-testid="close-form-btn"><X className="h-5 w-5" /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Area Name *</label>
                  <input data-testid="area-name-input" type="text" value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g. Greater Manchester" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                    <select data-testid="area-status-select" value={formData.status} onChange={e => setFormData(p => ({...p, status: e.target.value}))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                      <option value="early_interest">Early Interest</option>
                      <option value="priority">Priority</option>
                      <option value="live">Live</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">County</label>
                    <input data-testid="area-county-input" type="text" value={formData.county} onChange={e => setFormData(p => ({...p, county: e.target.value}))}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g. Surrey" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Town/City</label>
                  <input data-testid="area-town-input" type="text" value={formData.town_city} onChange={e => setFormData(p => ({...p, town_city: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g. Manchester" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Postcode Prefixes (comma-separated)</label>
                  <input data-testid="area-prefixes-input" type="text" value={formData.postcode_prefixes} onChange={e => setFormData(p => ({...p, postcode_prefixes: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g. M, OL, BL, SK" />
                  <p className="text-xs text-slate-400 mt-1">Letter prefix for broad matching (e.g. SW, KT)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Postcode Districts (comma-separated)</label>
                  <input data-testid="area-districts-input" type="text" value={formData.postcode_districts} onChange={e => setFormData(p => ({...p, postcode_districts: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="e.g. SW1A, SW1V, EC1A" />
                  <p className="text-xs text-slate-400 mt-1">Specific outward codes for precise matching</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea data-testid="area-notes-input" value={formData.notes} onChange={e => setFormData(p => ({...p, notes: e.target.value}))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500" rows={2} placeholder="Internal notes about this area..." />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
                  <button type="submit" disabled={saving} data-testid="save-area-btn"
                    className="px-5 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors flex items-center gap-2">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    {editingArea ? 'Update Area' : 'Create Area'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Content: Cards View or Heatmap View */}
        {activeTab === 'heatmap' ? (
          <ExpansionHeatmap areas={mergedHeatmapAreas} />
        ) : (
          <>
            {filteredAreas.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <AlertTriangle className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 font-medium">No areas found</p>
                <p className="text-slate-400 text-sm mt-1">Add areas to start tracking regional expansion</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="areas-grid">
                {filteredAreas.map(area => (
                  <AreaCard key={area.region_id} area={area} onEdit={handleEdit} onDelete={handleDelete} onStatusChange={handleStatusChange} />
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}


function KindCard({ label, tone, data, onClick, active, testId }) {
  const tones = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  const cls = tones[tone] || tones.slate;
  const ring = active ? 'ring-2 ring-orange-400' : '';
  const total = data?.total || 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-3 rounded-lg border ${cls} ${ring} transition hover:shadow-sm`}
    >
      <div className="text-xs uppercase tracking-wide opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-1">{total}</div>
      <div className="text-xs mt-0.5 opacity-80">
        {(data?.tradespeople || 0)} trades · {(data?.employers || 0)} employers
      </div>
    </button>
  );
}

function ToggleChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
        active
          ? 'bg-slate-900 text-white border-slate-900'
          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}
